import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getDarkMode } from 'selectors/theme';
import vector from './vector.svg';
import vectorDark from './vector-dark.svg';
import remove from './remove.svg';
import removeDark from './remove-dark.svg';
import './MultipleDropdown.css';
import OutsideAlerter from 'components/shared/OutsideAlerter';
import Input from 'components/shared/input/Input';

// Matches the whole search string against a simple, deliberately
// permissive email shape (something@something.something). This is a
// UI-level sanity check only, NOT validation -- the backend is the
// authority (Rfas::Shared::SetExternalCcs / RfaExternalAccess) and
// re-validates and normalises the address independently.
const DEFAULT_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class MultipleDropdown extends Component {
  static propTypes = {
    options: PropTypes.arrayOf(
      PropTypes.shape({
        className: PropTypes.string,
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      })
    ).isRequired,
    currentValues: PropTypes.array.isRequired,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    valid: PropTypes.bool,
    search: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    // Opt-in only -- every existing call site leaves these undefined,
    // so `allowCustomOption` is falsy and every branch below behaves
    // exactly as it did before this prop existed. Added for the
    // External CC / Assign-To feature's "type an address that isn't
    // in the directory" affordance
    // (docs/external-cc-implementation-plan-2026-09-04.md), without
    // changing the directory-only contract for any other caller of
    // this shared component.
    allowCustomOption: PropTypes.bool,
    onCreateOption: PropTypes.func,
    isValidCustomOption: PropTypes.func,
    customOptionLabel: PropTypes.func,
  };

  static defaultProps = {
    valid: true,
    search: true,
    placeholder: '',
    allowCustomOption: false,
    isValidCustomOption: (value) => DEFAULT_EMAIL_PATTERN.test(value),
    customOptionLabel: (value) => `Add "${value}"`,
  };

  constructor(props) {
    super(props);

    this.state = {
      showOptions: false,
      filteredOptions: null,
      search: '',
    };
  }

  renderOptionsPopup = () => {
    const { options, currentValues, onChange } = this.props;
    const { filteredOptions } = this.state;

    return (filteredOptions || options).map((opt) => {
      const selected = !!currentValues.find((val) => val[0] === opt.id);
      return (
        <React.Fragment key={opt.id}>
          <button
            type="button"
            className={`button-option${selected ? ' selected' : ''} ${
              opt.className || ''
            }`}
            onClick={() => {
              if (!selected) {
                onChange(currentValues.concat([[opt.id, opt.label]]));
              } else {
                onChange(currentValues.filter((val) => val[0] !== opt.id));
              }
            }}
          >
            {opt.label}
          </button>
          <div className="menu-item-separator"></div>
        </React.Fragment>
      );
    });
  };

  // Opt-in only: returns null unless `allowCustomOption` is set AND the
  // current search text passes `isValidCustomOption` AND it doesn't
  // already exactly match an existing option's label (typing the exact
  // name of a directory contact should just let them pick it normally).
  renderCustomOption = () => {
    const {
      allowCustomOption,
      options,
      isValidCustomOption,
      customOptionLabel,
      onCreateOption,
    } = this.props;
    const { search } = this.state;
    if (!allowCustomOption) return null;

    const trimmed = (search || '').trim();
    if (!trimmed || !isValidCustomOption(trimmed)) return null;

    const matchesExistingOption = options.some(
      (opt) =>
        typeof opt.label === 'string' &&
        opt.label.toLowerCase() === trimmed.toLowerCase()
    );
    if (matchesExistingOption) return null;

    return (
      <React.Fragment>
        <button
          type="button"
          className="button-option custom-option"
          onClick={() => {
            if (onCreateOption) onCreateOption(trimmed);
            this.setState({
              search: '',
              filteredOptions: null,
              showOptions: false,
            });
          }}
        >
          {customOptionLabel(trimmed)}
        </button>
        <div className="menu-item-separator"></div>
      </React.Fragment>
    );
  };

  handleToggleOptions = () => {
    this.setState({ showOptions: !this.state.showOptions });
  };

  renderLabel = (value) => {
    if (value[1]) {
      return value[1];
    } else {
      const { options } = this.props;
      const selectedOption = options.find((opt) => opt.id === value[0]);
      return selectedOption && selectedOption.label;
    }
  };

  selectedLabels = () => {
    const { currentValues, darkMode } = this.props;
    return (
      <div className="label-preview-container">
        {currentValues.map((value, index) => (
          <div key={index} className="label-preview-item">
            <span className="label-preview-item-text">
              {this.renderLabel(value)}
            </span>
            <img
              className="label-preview-item-remove"
              src={darkMode ? removeDark : remove}
              alt="remove"
              onClick={(event) => this.removeSelection(event, value)}
            />
          </div>
        ))}
      </div>
    );
  };

  removeSelection = (event, value) => {
    const { onChange, currentValues } = this.props;

    event.stopPropagation();
    const newValues = currentValues.filter((val) => val[0] !== value[0]);
    onChange(newValues);
  };

  allowSearching = () => {
    const { options, search } = this.props;
    return search && options.every((opt) => typeof opt.label === 'string');
  };

  handleSearchChange = (value) => {
    const { options } = this.props;
    const filter = new RegExp(value.toLowerCase(), 'g');
    const newFilteredOptions = [
      ...options.filter((opt) => filter.test(opt.label.toLowerCase())),
    ];
    this.setState({ filteredOptions: newFilteredOptions, search: value });
  };

  render() {
    const {
      options,
      placeholder,
      className,
      dropdownClassName,
      currentValues,
      valid,
      darkMode,
    } = this.props;
    const { showOptions, search, filteredOptions } = this.state;
    const allowSearching = this.allowSearching();
    return (
      <div
        className={`multiple-dropdown${className ? ` ${className}` : ''} ${
          darkMode ? ' dark-mode' : ''
        } ${!valid ? 'invalid' : ''}`}
      >
        <div
          className={`dropdown-control ${
            dropdownClassName ? dropdownClassName : 'default-dropdown-control'
          }`}
          onClick={this.handleToggleOptions}
        >
          {currentValues.length > 0 ? (
            this.selectedLabels()
          ) : (
            <div className="dropdown-control-placeholder">{placeholder}</div>
          )}
          <img
            className="dropdown-menu-selector-arrow"
            src={darkMode ? vectorDark : vector}
            alt="arrow"
          />
        </div>
        {showOptions && (
          <OutsideAlerter onClickOutside={this.handleToggleOptions}>
            <div className="options">
              {allowSearching && (
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={this.handleSearchChange}
                />
              )}
              {(filteredOptions || options).length > 0 ||
              this.renderCustomOption() ? (
                <div className="options-buttons">
                  {this.renderOptionsPopup()}
                  {this.renderCustomOption()}
                </div>
              ) : (
                <div className="no-options">No results found</div>
              )}
            </div>
          </OutsideAlerter>
        )}
      </div>
    );
  }
}

export default connect((state) => {
  return {
    darkMode: getDarkMode(state),
  };
})(MultipleDropdown);
